import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brandmark, Wordmark } from "@/components/Brand";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { MailCheck } from "lucide-react";

/**
 * /verify-email — landing page after a fresh signup when email confirmation
 * is enabled on the Supabase project.
 *
 * The user sits here until either:
 *  1. They click the verification link (which routes them to /auth/callback
 *     and then back into the app), OR
 *  2. They request a resend.
 *
 * We also auto-redirect once `emailVerified` flips to true — this handles
 * the case where the user confirms in another tab and comes back to this
 * one (the auth listener will fire and we bounce).
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, emailVerified, hydrated, signOut, resendVerificationEmail } = useAuth();
  const [cooldown, setCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    if (!user) {
      // No session at all — send them to the sign-in page.
      navigate("/auth", { replace: true });
      return;
    }
    if (emailVerified) {
      navigate("/", { replace: true });
    }
  }, [user, emailVerified, hydrated, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleResend() {
    setSubmitting(true);
    const { error, cooldown: waitSeconds } = await resendVerificationEmail();
    setSubmitting(false);
    if (waitSeconds > 0) {
      setCooldown(waitSeconds);
      toast({
        title: "Hang on a second",
        description: `You can resend in ${waitSeconds}s.`,
      });
      return;
    }
    if (error) {
      toast({ title: "Couldn't resend", description: error.message, variant: "destructive" });
      return;
    }
    setCooldown(60);
    toast({ title: "Sent", description: "We sent another verification link to your inbox." });
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto flex min-h-screen max-w-[1080px] flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <Brandmark size={32} />
          <Wordmark className="text-[24px]" />
        </div>

        <div className="w-full max-w-[460px] rounded-xl border border-line bg-canvas p-8 text-center shadow-[0_1px_2px_rgba(26,25,21,.04)]">
          <div
            className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: "hsl(var(--moss) / 0.12)" }}
            aria-hidden="true"
          >
            <MailCheck className="h-5 w-5 text-moss" />
          </div>

          <h1 className="mt-5 font-serif text-[26px] leading-[1.15] text-ink">
            Verify your <em className="italic text-accent-rf">email</em>
          </h1>
          <p className="mt-2 text-[13px] text-ink-3">
            We sent a confirmation link to{" "}
            <span className="font-medium text-ink">{user?.email ?? "your inbox"}</span>. Click it to finish
            setting up your Routeflow workspace.
          </p>

          <button
            type="button"
            onClick={handleResend}
            disabled={submitting || cooldown > 0}
            className="mt-6 w-full rounded-md border border-line bg-canvas px-4 py-2.5 text-[13px] font-medium text-ink transition-colors duration-[120ms] hover:bg-canvas-2 disabled:opacity-50"
          >
            {submitting
              ? "Sending…"
              : cooldown > 0
              ? `Resend available in ${cooldown}s`
              : "Resend verification email"}
          </button>

          <div className="mt-6 flex items-center justify-center gap-3 text-[12px] text-ink-3">
            <Link to="/auth" className="underline-offset-2 hover:underline">
              Use a different email
            </Link>
            <span>·</span>
            <button
              type="button"
              onClick={() => signOut()}
              className="underline-offset-2 hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
